(ns dre.pages.my-searches.signals.events
  (:require
   ["moment" :as moment]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [day8.re-frame.tracing :refer-macros [fn-traced]] 
   [dre.app.router :as router]
   [dre.coll :refer [some-hash-map index-by]]
   [dre.components.flash.events :as-alias flash]
   [dre.i18n.views :refer [tr]]
   [dre.network :as-alias network]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [dre.utils.query :as q]
   [dre.shared.interface.search.util :refer [blunt-key normalized-blunt]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path :signals)

(defn api-params
  [signal]
  (let [{:keys [:signal/title :signal/description
                :query/search-query
                :signal/settings]} signal]
    {:title title
     :description (or description "")
     :query search-query
     :settings settings}))

(defn signal
  "Returns signal from a given response."
  [response]
  (let [{:keys [:signal-uuid :query :title :description
                :last-visited :last-viewed :last-seen-article
                :new-articles :settings :created]} response
        search-query (q/coerce query)]
    (some-hash-map
     :query/search-query search-query
     :signal/uuid signal-uuid
     :signal/title title
     :signal/description description
     :signal/last-visited last-visited
     :signal/last-viewed last-viewed
     :signal/recent-activity (or last-viewed created)
     :signal/last-seen-article last-seen-article
     :signal/new-articles new-articles
     :signal/settings settings)))

(defn signalsv
  "Returns vector of signals from a given response"
  [response]
  (mapv signal response))

(def uuid->signal-path
  [:data :uuid->signal])

(def blunt->signal-path
  [:data :blunt->signal])

(defn query->labels
  [search-query]
  (let [texts (volatile! [])]
    (walk/postwalk
     (fn [x]
       (if (map? x)
         (let [text (case (:pill/type x)
                      :term
                      (str/join
                       " "
                       ((juxt :pill/canonical-label
                              :pill/label)
                        x))
                      :text (:pill/query x)
                      (:pill/label x))]
           (when text (vswap! texts conj text))
           x)
         x))
     search-query)
    @texts))


;; -- Events

(rf/reg-event-fx
 ::fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [_]]
  {:db {:state :loading}
   ::network/request [{:endpoint :saved/signals}
                      {:on-success [::success-fetch]
                       :on-failure [::failure-fetch]}]}))

(rf/reg-event-fx
 ::success-fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (if-let [signals (:signals (:body response))]
    (let [next-signals (signalsv signals)]
      {:db (-> db
               (assoc-in [:data :signals] next-signals)
               (assoc :state :success)
               (assoc-in blunt->signal-path
                         (index-by (comp blunt-key :query/search-query) next-signals))
               (assoc-in uuid->signal-path
                         (index-by :signal/uuid
                                   next-signals)))})
    {:db (-> db
             (assoc :state :success))})))

(rf/reg-event-fx
 ::failure-fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (-> db
             (assoc :state :failure)
             (assoc :data nil))
     :dispatch-n [[:dre.network/default-failure opts]]})))

(rf/reg-event-fx
 ::signal-filter
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [filter]]
  {:db (-> db
           (assoc-in [:data :signal-filter] filter))}))

(rf/reg-event-fx
 ::delete
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [uuids]]
  (if (seq uuids)
    (let [current (first uuids)
          r (vec (rest uuids))]
      {:db (-> db
               (assoc :state :loading))
       ::network/request [{:endpoint :saved/delete-signals
                           :context {:id current}}
                          {:on-success [::success-delete {:uuids r}]
                           :on-failure [::failure-delete]}]})
    nil)))

(rf/reg-event-fx
 ::success-delete
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [:uuids ::fetch-fx/response]}]]
  (if (seq uuids)
    {:dispatch [::delete uuids]}
    {:db (-> db
             (assoc :state :success))
     :dispatch-n [[::fetch]]})))

(rf/reg-event-fx
 ::failure-delete
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (-> db
           (assoc :state :failure))
   :dispatch-n [[::fetch]
                [:dre.network/default-failure opts]]}))


(rf/reg-event-fx
 ::mark-viewed
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [signal]]
  (let [{:keys [signal/uuid query/search-query]} signal
        blunt (normalized-blunt search-query)]
    {::network/request [{:endpoint :saved/mark-viewed-signal
                         :context {:id uuid}}
                        {:body {"signal/viewed" (moment)}
                         :on-success [::success-mark-viewed]}]})))
 
(rf/reg-event-fx
 ::success-mark-viewed
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  nil))
 

(rf/reg-event-fx
 ::mark-visited
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [last-seen signal]]
  (let [{:keys [signal/uuid query/search-query]} signal]
    {::network/request [{:endpoint :saved/mark-visited-signal
                         :context {:id uuid}}
                        {:body {"article/serial" last-seen}
                         :on-success [::success-mark-visited]}]})))

(rf/reg-event-fx
 ::success-mark-visited
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  nil))
 

(rf/reg-event-fx
 ::success-edit
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  {:dispatch-n [[::fetch]]}))
 
(rf/reg-event-fx
 ::failure-edit
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response] :as opts}]]
  {:db (-> db
           (assoc :state :failure))
   :dispatch-n [[:dre.network/default-failure opts]]}))

(rf/reg-event-fx
 ::edit
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [uuid signal]]
  (let [blunt (normalized-blunt (:query/search-query signal))
        blunt->signal (get-in db blunt->signal-path)
        prev-signal (get blunt->signal blunt)]
    (if (or
         ;; query did not change
         (= uuid (:signal/uuid prev-signal))
         ;; new query isn't present in other signal
         (not prev-signal))
      {::network/request [{:endpoint :saved/update-signal
                           :context {:id uuid}}
                          {:body (stringify-keys-with-namespaces (api-params signal))
                           :on-success [::success-edit]
                           :on-failure [::failure-edit]}]
       :db {:state :loading}
       :dispatch [::router/redirect-to-path (router/->route :signals)]}
      {:dispatch [::flash/show
                  [:span [:b [tr "There already seems to be saved search with the same query."] [:br]]]
                  :severity :error]}))))

(rf/reg-event-fx
 ::add
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [signal]]
  (let [blunt (normalized-blunt (:query/search-query signal))
        blunt->signal (get-in db blunt->signal-path)
        prev-signal (get blunt->signal blunt)]
    (if-not prev-signal
      {::network/request [{:endpoint :saved/add-signal}
                          {:body (stringify-keys-with-namespaces (api-params signal))
                           :on-success [::success-edit]
                           :on-failure [::failure-edit]}]
       :db {:state :loading}
       :dispatch [::router/redirect-to-path (router/->route :signals)]}
      {:dispatch [::flash/show
                  [:span [:b [tr "There already seems to be saved search with the same query."] [:br]]]
                  :severity :error]}))))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (db-path db)))

(defn in?
  "Determines if an element is in a given sequence."
  [seq elm]
  (some #(= elm %) seq))

(rf/reg-sub
 ::signal-filter
 :<- [::state]
 (fn [state _]
   (let [data (get-in state [:data :signal-filter] nil)]
     data)))

(rf/reg-sub
 ::signals-without-filter
 :<- [::state]
 (fn [state _]
   (let [data (get-in state [:data :signals] nil)]
     data)))

(rf/reg-sub
 ::signals
 :<- [::state]
 :<- [::signal-filter]
 (fn [[state signal-filter] _]
   (let [signal-filter-post (-> (or signal-filter "")
                                (str/trim)
                                (str/lower-case))
         data (get-in state [:data :signals] nil)
         data (remove #(in? [:regdocs :effects] (get-in % [:query/search-query :query/type])) data)
         data (if (or (not signal-filter-post) (str/blank? signal-filter-post))
                data
                (filter (fn [{:keys [:signal/title :query/search-query]}]
                          (let [labels (query->labels search-query)
                                label-str (str/join labels)
                                label-match? (or (str/includes? (str/lower-case (or title "")) signal-filter-post)
                                                 (str/includes? (str/lower-case (or label-str "")) signal-filter-post))]
                            label-match?)) data))]
     data)))

(rf/reg-sub
 ::loading?
 :<- [::state]
 (fn [state _]
   (let [data (get-in state [:state] nil)]
     (= :loading data))))

(rf/reg-sub
 ::uuid->simple-signal
 :<- [::state]
 (fn [db [_ uuid]]
   (get-in db (conj uuid->signal-path uuid))))
